/**
 * MeshCore companion over Bluetooth LE (Nordic UART Service).
 *
 * Built on the Capacitor BLE bridge, so one transport serves Chrome / Edge
 * (Web Bluetooth) and the iOS / Android apps (native plugin). Over BLE each
 * GATT write is one frame and each notification is one frame: no framing.
 */

import {
  type BridgedBleDevice,
  connect as bleConnect,
  disconnect as bleDisconnect,
  isBLEAvailable,
  requestDevice,
  startNotifications,
  uint8ToDataView,
  write,
} from '../bluetooth/capacitor-ble-bridge'
import type { CompanionTransport } from './client'

export const NUS_SERVICE = '6e400001-b5a3-f393-e0a9-e50e24dcca9e'
/** app -> radio */
export const NUS_RX = '6e400002-b5a3-f393-e0a9-e50e24dcca9e'
/** radio -> app */
export const NUS_TX = '6e400003-b5a3-f393-e0a9-e50e24dcca9e'

/**
 * Measured 2026-10-14 (Heltec V4 BLE companion <-> BlueZ): every notification
 * can arrive twice within ~10 ms. A real reply needs a write round trip
 * (>= ~50 ms), so a byte-identical frame inside this window is the duplicate.
 */
const DUPLICATE_WINDOW_MS = 30

export function isMeshCoreBleAvailable(): boolean {
  return isBLEAvailable()
}

export interface BleCompanionLink {
  transport: CompanionTransport
  device: BridgedBleDevice
}

/** Show the system chooser for a MeshCore radio. Call from a user gesture. */
export async function chooseBleRadio(): Promise<BridgedBleDevice> {
  return requestDevice({
    services: [NUS_SERVICE],
    optionalServices: [NUS_SERVICE],
    namePrefix: 'MeshCore-',
  })
}

/**
 * Connect to a radio the user already chose and subscribe. Needs no user
 * gesture, so the session can reconnect (e.g. to apply radio settings,
 * which only take on a fresh connection).
 */
export async function openBleCompanionOn(
  device: BridgedBleDevice,
  onDisconnect: () => void
): Promise<BleCompanionLink> {
  await bleConnect(device, () => onDisconnect())

  let sink: (frame: Uint8Array) => void = () => undefined
  let last: Uint8Array | null = null
  let lastAt = 0

  const stop = await startNotifications(device, NUS_SERVICE, NUS_TX, (view) => {
    // Copy: the platform may reuse the buffer behind the DataView.
    const frame = new Uint8Array(view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength))
    const now = Date.now()
    if (last && now - lastAt < DUPLICATE_WINDOW_MS && sameBytes(frame, last)) return
    last = frame
    lastAt = now
    sink(frame)
  })

  const transport: CompanionTransport = {
    onFrame: (cb) => {
      sink = cb
    },
    send: (frame) => write(device, NUS_SERVICE, NUS_RX, uint8ToDataView(frame)),
    close: async () => {
      try {
        await stop()
      } finally {
        await bleDisconnect(device)
      }
    },
  }
  return { transport, device }
}

/** Chooser + connect in one step. Call from a user gesture. */
export async function openBleCompanion(onDisconnect: () => void): Promise<BleCompanionLink> {
  return openBleCompanionOn(await chooseBleRadio(), onDisconnect)
}

function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
  return true
}
